import { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Heart, ShoppingCart, X, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { useCart } from '../CartContext';

const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:5000'
  : 'https://kiyafet-magazasi-backend.vercel.app';

interface FavoriteItem {
  id: number;
  ad: string;
  fiyat: number;
  eski_fiyat?: number;
  resimler?: string[];
  kategori: string;
  stok_durumu: boolean;
}

export function Favorites() {
  const [favorites, setFavorites] = useState<FavoriteItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'price-low' | 'price-high'>('newest');
  const { addToCart } = useCart();

  useEffect(() => {
    const kullanici = JSON.parse(localStorage.getItem('kullanici') || 'null');
    if (!kullanici) {
      setLoading(false);
      toast.error('Favorileri görmek için giriş yapın');
      window.location.href = '/hesabim';
      return;
    }
    fetch(`${API_URL}/api/favorilerim/${kullanici.id}`)
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setFavorites(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const removeFromFavorites = async (id: number) => {
    const kullanici = JSON.parse(localStorage.getItem('kullanici') || 'null');
    if (!kullanici) return;
    try {
      await fetch(`${API_URL}/api/favori-cikar/${kullanici.id}/${id}`, { method: 'DELETE' });
      setFavorites(favorites.filter(item => item.id !== id));
      toast.success('Ürün favorilerden çıkarıldı');
    } catch { toast.error('Bir hata oluştu'); }
  };

  const handleAddToCart = (item: FavoriteItem) => {
    if (!item.stok_durumu) { toast.error('Bu ürün stokta yok'); return; }
    const resim = item.resimler?.[0] || 'https://via.placeholder.com/400x533?text=Resim+Yok';
    addToCart({ id: item.id, name: item.ad, price: item.fiyat, image: resim });
  };

  const addAllToCart = () => {
    const inStockItems = favorites.filter(f => f.stok_durumu !== false);
    if (inStockItems.length === 0) { toast.error('Stokta ürün yok'); return; }
    inStockItems.forEach(item => {
      const resim = item.resimler?.[0] || 'https://via.placeholder.com/400x533?text=Resim+Yok';
      addToCart({ id: item.id, name: item.ad, price: item.fiyat, image: resim });
    });
    toast.success(`${inStockItems.length} ürün sepete eklendi!`);
  };

  const clearAllFavorites = async () => {
    if (!window.confirm('Tüm favorilerinizi silmek istediğinizden emin misiniz?')) return;
    const kullanici = JSON.parse(localStorage.getItem('kullanici') || 'null');
    if (!kullanici) return;
    for (const item of favorites) {
      await fetch(`${API_URL}/api/favori-cikar/${kullanici.id}/${item.id}`, { method: 'DELETE' }).catch(() => {});
    }
    setFavorites([]);
    toast.success('Tüm favoriler temizlendi');
  };

  const categories = ['all', ...Array.from(new Set(favorites.map(f => f.kategori).filter(Boolean)))];

  let filteredFavorites = selectedCategory === 'all' ? favorites : favorites.filter(f => f.kategori === selectedCategory);
  if (sortBy === 'price-low') filteredFavorites = [...filteredFavorites].sort((a, b) => a.fiyat - b.fiyat);
  else if (sortBy === 'price-high') filteredFavorites = [...filteredFavorites].sort((a, b) => b.fiyat - a.fiyat);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div></main>
        <Footer />
      </div>
    );
  }

  if (favorites.length === 0) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center px-4 py-16">
          <div className="text-center max-w-md">
            <div className="size-32 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-6"><Heart className="size-16 text-pink-400" /></div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Favori Listeniz Boş</h2>
            <p className="text-gray-600 mb-8">Henüz favori ürününüz yok. Beğendiğiniz ürünleri favorilere ekleyerek daha sonra kolayca bulabilirsiniz.</p>
            <a href="/" className="inline-flex items-center justify-center gap-2 bg-pink-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-pink-700 transition-colors">Alışverişe Başla</a>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <main className="flex-1 py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Favorilerim</h1>
                <p className="text-gray-600">{filteredFavorites.length} ürün listeleniyor</p>
              </div>
              <div className="flex gap-3">
                <button onClick={addAllToCart} className="flex items-center gap-2 bg-pink-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-pink-700 transition-colors">
                  <ShoppingCart className="size-5" /><span className="hidden md:inline">Tümünü Sepete Ekle</span><span className="md:hidden">Sepete Ekle</span>
                </button>
                <button onClick={clearAllFavorites} className="flex items-center gap-2 bg-white text-gray-700 px-6 py-3 rounded-lg font-semibold border-2 border-gray-300 hover:border-red-300 hover:text-red-600 transition-colors">
                  <X className="size-5" /><span className="hidden md:inline">Tümünü Temizle</span>
                </button>
              </div>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2"><Filter className="size-4 text-gray-500" /><span className="text-sm font-semibold text-gray-700">Kategori</span></div>
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {categories.map((cat) => (
                      <button key={cat} onClick={() => setSelectedCategory(cat)}
                        className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${selectedCategory === cat ? 'bg-pink-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                        {cat === 'all' ? 'Tümü' : cat}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="md:w-64">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Sırala</label>
                  <select value={sortBy} onChange={(e) => setSortBy(e.target.value as any)} className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500">
                    <option value="newest">En Yeni</option>
                    <option value="price-low">Fiyat (Düşük - Yüksek)</option>
                    <option value="price-high">Fiyat (Yüksek - Düşük)</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {filteredFavorites.length === 0 ? (
            <div className="bg-white rounded-lg p-12 text-center"><p className="text-gray-600">Bu kategoride favori ürün bulunamadı.</p></div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {filteredFavorites.map((item) => {
                const resim = item.resimler?.[0] || 'https://via.placeholder.com/400x533?text=Resim+Yok';
                const inStock = item.stok_durumu !== false;
                return (
                  <div key={item.id} className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow group">
                    <div className="relative aspect-[3/4] bg-gray-100">
                      <a href={`/urun/${item.id}`}><img src={resim} alt={item.ad} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></a>
                      {!inStock && <div className="absolute top-3 left-3 bg-gray-900 text-white px-3 py-1 rounded-full text-xs font-bold">Stokta Yok</div>}
                      {item.eski_fiyat && inStock && <div className="absolute top-3 left-3 bg-pink-600 text-white px-3 py-1 rounded-full text-xs font-bold">İNDİRİM</div>}
                      <button onClick={() => removeFromFavorites(item.id)} className="absolute top-3 right-3 size-9 bg-white rounded-full flex items-center justify-center hover:bg-red-50 transition-colors shadow-md">
                        <Heart className="size-5 fill-pink-600 text-pink-600" />
                      </button>
                      <div className="absolute bottom-0 left-0 right-0 p-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => handleAddToCart(item)} disabled={!inStock}
                          className="flex-1 bg-pink-600 text-white py-2 rounded-lg font-semibold hover:bg-pink-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-1">
                          <ShoppingCart className="size-4" /><span className="text-xs">Sepete Ekle</span>
                        </button>
                      </div>
                    </div>
                    <div className="p-3">
                      <span className="text-xs text-gray-500 mb-1 block">{item.kategori}</span>
                      <a href={`/urun/${item.id}`} className="block"><h3 className="font-medium text-gray-900 mb-2 text-sm line-clamp-2 hover:text-pink-600 transition-colors">{item.ad}</h3></a>
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-bold text-pink-600">₺{Number(item.fiyat).toLocaleString('tr-TR')}</span>
                        {item.eski_fiyat && <span className="text-sm text-gray-400 line-through">₺{Number(item.eski_fiyat).toLocaleString('tr-TR')}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-8 bg-gradient-to-r from-pink-100 to-purple-100 rounded-xl p-6">
            <h3 className="font-semibold text-gray-900 mb-2">💡 İpucu</h3>
            <p className="text-gray-700 text-sm">Favori ürünlerinizi arkadaşlarınızla paylaşabilir, fiyat değişikliklerinden haberdar olabilirsiniz.</p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
